import { Trans } from '@lingui/react/macro';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

import Loader from '@/components/Loader';
import { DrawerHeader } from '@/design_system/Drawer/Drawer';
import Stack from '@/design_system/Stack';
import { StepperNumber } from '@/design_system/Stepper/StepperNumber/StepperNumber';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useArticleName } from '@/models/article';
import { ArticleWithRelations, RequestWithRelations } from '@/models/request';
import { useMultiStepCreationContext } from '@/routes/Requests/contexts/MultiStepCreationContext';
import useViewPort from '@/utils/useViewport';

const NewArticleHeader = ({
  request,
  article,
  isEditing,
}: {
  request: RequestWithRelations;
  article: ArticleWithRelations;
  isEditing: boolean;
}) => {
  const { isMobile } = useViewPort();
  const isFetching = useIsFetching({ queryKey: ['requests', request.id] });
  const isMutating = useIsMutating({ mutationKey: ['articles', article.id] });
  const { steps, currentStep, currentStepIndex, getHeaderSteps } = useMultiStepCreationContext();

  const name = useArticleName({ article });
  const currentStepNumber = currentStepIndex + 1;
  const stepsLength = steps.length;
  const currentStepName = currentStep.name;

  return (
    <DrawerHeader
      subtitle={
        !isMobile ? (
          <Stack padding={'0.5rem 0 0'} style={{ width: '100%' }}>
            <StepperNumber stepperNumberItems={getHeaderSteps()} />
          </Stack>
        ) : (
          <Paragraph weight="medium">
            <Trans id="requests.new.mobile.multi-step.subtitle">
              Step {`${currentStepNumber}/${stepsLength}: ${currentStepName}`}
            </Trans>
          </Paragraph>
        )
      }
    >
      <Stack row alignItems="center">
        <Title size="200" as="h1" ellipsis>
          {isEditing ? (
            <Trans id="requests.new.articles.drawer.title-edit">Edit {name}</Trans>
          ) : (
            <Trans id="requests.new.articles.drawer.title">Add an item</Trans>
          )}
        </Title>
        {(isFetching > 0 || isMutating > 0) && (
          <Loader style={{ fontSize: '1.25rem' }} delay={500} />
        )}
      </Stack>
    </DrawerHeader>
  );
};

export default NewArticleHeader;
